import {
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import type { User, UserRole, UserStatus, Business, CreateUserInput } from '@/types';
import { createLogger } from '@/utils/debug';
import { usersCollection } from '@/config/collections';

const log = createLogger('Users');

// ===========================================
// Users Collection Reference
// ===========================================

const usersRef = usersCollection(db);

// ===========================================
// Helper: Convert Firestore document to User
// ===========================================

function docToUser(doc: any): User {
  const data = doc.data();
  return {
    id: doc.id,
    email: data.email,
    displayName: data.displayName,
    photoURL: data.photoURL,
    role: data.role as UserRole,
    status: data.status as UserStatus,
    tenantId: data.tenantId,
    preferredBusiness: data.preferredBusiness as Business || 'azdisaster',
    mondayPersonId: data.mondayPersonId,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  };
}

// ===========================================
// User CRUD Operations
// ===========================================

/**
 * Get all users (sorted by display name)
 */
export async function getUsers(): Promise<User[]> {
  return log.operation('getUsers', async () => {
    const q = query(usersRef, orderBy('displayName', 'asc'));
    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(docToUser);
    log.debug('getUsers completed', { count: users.length });
    return users;
  });
}

/**
 * Get users by role
 */
export async function getUsersByRole(role: UserRole): Promise<User[]> {
  const q = query(usersRef, where('role', '==', role), orderBy('displayName', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToUser);
}

/**
 * Get users by status
 */
export async function getUsersByStatus(status: UserStatus): Promise<User[]> {
  const q = query(usersRef, where('status', '==', status), orderBy('displayName', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToUser);
}

/**
 * Get a single user by ID
 */
export async function getUser(id: string): Promise<User | null> {
  return log.operation('getUser', async () => {
    const docRef = doc(usersRef, id);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      log.debug('getUser not found', { id });
      return null;
    }

    log.debug('getUser found', { id });
    return docToUser(snapshot);
  }, { id });
}

/**
 * Get user by email
 */
export async function getUserByEmail(email: string): Promise<User | null> {
  const q = query(usersRef, where('email', '==', email.toLowerCase()), limit(1));
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  return docToUser(snapshot.docs[0]);
}

/**
 * Create a new user document (typically after Firebase Auth signup)
 */
export async function createUser(
  userId: string,
  input: CreateUserInput,
  tenantId: string = 'default'
): Promise<User> {
  return log.operation('createUser', async () => {
    const userDoc = {
      email: input.email.toLowerCase(),
      displayName: input.displayName,
      role: input.role,
      status: 'pending' as UserStatus,
      tenantId,
      preferredBusiness: 'azdisaster' as Business,
      mondayPersonId: input.mondayPersonId || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(usersRef, userId), userDoc);

    const created = await getDoc(doc(usersRef, userId));
    const user = docToUser(created);
    log.info('createUser success', { id: user.id, email: user.email, role: user.role });
    return user;
  }, { userId, email: input.email, role: input.role });
}

/**
 * Update an existing user
 */
export async function updateUser(
  id: string,
  updates: Partial<{
    displayName: string;
    role: UserRole;
    status: UserStatus;
    mondayPersonId: string | null;
    preferredBusiness: Business;
  }>
): Promise<void> {
  return log.operation('updateUser', async () => {
    const docRef = doc(usersRef, id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    log.info('updateUser success', { id, fields: Object.keys(updates) });
  }, { id, fields: Object.keys(updates) });
}

/**
 * Activate a user
 */
export async function activateUser(id: string): Promise<void> {
  await updateUser(id, { status: 'active' });
}

/**
 * Deactivate a user
 */
export async function deactivateUser(id: string): Promise<void> {
  await updateUser(id, { status: 'inactive' });
}

/**
 * Delete a user (owner only)
 */
export async function deleteUser(id: string): Promise<void> {
  return log.operation('deleteUser', async () => {
    const docRef = doc(usersRef, id);
    await deleteDoc(docRef);
    log.info('deleteUser success', { id });
  }, { id });
}

/**
 * Update user's monday.com person ID mapping
 */
export async function updateMondayPersonId(
  id: string,
  mondayPersonId: string | null
): Promise<void> {
  await updateUser(id, { mondayPersonId });
}

// ===========================================
// Utility Functions
// ===========================================

/**
 * Get user statistics
 */
export async function getUserStats(): Promise<{
  total: number;
  byRole: Record<UserRole, number>;
  byStatus: Record<UserStatus, number>;
}> {
  const users = await getUsers();

  const byRole: Record<UserRole, number> = {
    owner: 0,
    admin: 0,
    rep: 0,
  };

  const byStatus: Record<UserStatus, number> = {
    active: 0,
    inactive: 0,
    pending: 0,
  };

  for (const user of users) {
    byRole[user.role] = (byRole[user.role] || 0) + 1;
    byStatus[user.status] = (byStatus[user.status] || 0) + 1;
  }

  return {
    total: users.length,
    byRole,
    byStatus,
  };
}

/**
 * Get all sales reps (active users with rep role)
 */
export async function getSalesReps(): Promise<User[]> {
  return log.operation('getSalesReps', async () => {
    // Get all active users, then filter for rep/admin/owner in memory
    // This avoids needing an 'IN' query which requires a composite index
    const q = query(
      usersRef,
      where('status', '==', 'active'),
      orderBy('displayName', 'asc')
    );
    const snapshot = await getDocs(q);
    const reps = snapshot.docs
      .map(docToUser)
      .filter((u) => u.role === 'rep' || u.role === 'admin' || u.role === 'owner');
    log.debug('getSalesReps completed', { count: reps.length });
    return reps;
  });
}

// ===========================================
// User Invite Functions (Cloud Function calls)
// ===========================================

interface InviteUserInput {
  email: string;
  displayName: string;
  role: 'admin' | 'rep';
  mondayPersonId?: string;
}

interface InviteUserResult {
  success: boolean;
  userId?: string;
  message?: string;
}

/**
 * Invite a new user via email (calls Cloud Function)
 * Creates Firebase Auth user and sends invitation email via Brevo
 */
export async function inviteUser(input: InviteUserInput): Promise<InviteUserResult> {
  return log.operation('inviteUser', async () => {
    const sendUserInvite = httpsCallable<InviteUserInput, InviteUserResult>(
      functions,
      'crm_sendUserInvite'
    );

    const result = await sendUserInvite(input);
    log.info('inviteUser success', { email: input.email, role: input.role });
    return result.data;
  }, { email: input.email, role: input.role });
}
